import { Identifier, Timestamp } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Appointment } from '../appointment/_types';

interface FetchCalendarOptions {
  start: number;
  end: number;
  timeZone?: string;
  cancelled?: boolean;
  noAvailabilitySchedules?: boolean;
  location?: Identifier | string; // Can be comma-separated for multiple locations
  userId?: Identifier | string; // Can be comma-separated for multiple trainers
  type: 'appointments' | 'schedules';
  spotsRemaining?: number;
  serviceId?: Identifier | string; // Can be comma-separated for multiple services
  category?: Identifier | string; // Can be comma-separated for multiple categories
  resourceId?: Identifier | string; // Can be comma-separated for multiple booked assets
  trainerTag?: string; // Staff member tags
  currentUserId?: Identifier; // Current user ID for fallback
}

export interface CalendarAppointment {
  data: Appointment;
  date: Timestamp;
  startTime: string;
  endTime: string;
  image: string | null;
  text: string;
  objectId: Identifier;
  objectType: 'appointment';
  primaryTrainerIds: Identifier[];
  secondaryTrainerIds: Identifier[];
}

type FetchCalendarResponse = any[];

export const fetchCalendar = async ({
  type,
  start,
  end,
  timeZone,
  cancelled,
  location,
  noAvailabilitySchedules,
  userId,
  spotsRemaining,
  serviceId,
  category,
  resourceId,
  trainerTag,
}: FetchCalendarOptions): Promise<FetchCalendarResponse> => {
  // Conditional platform/user_id logic from v2 calendar-params.js
  // Day view: always use platform = true
  // Week/list view: depends on trainerId format and trainerTag
  let platform: boolean | undefined;
  let user_id: Identifier | string | undefined;
  let user_ids: string | undefined;

  // Week/list view
  const userIdStr = userId ? String(userId) : '';
  const hasMultipleTrainers = userIdStr.includes(',');

  if (hasMultipleTrainers) {
    if (trainerTag) {
      // Multiple trainers with tag filter: use user_ids
      user_ids = userIdStr;
    } else {
      // Multiple trainers without tag: use platform
      platform = true;
    }
  } else {
    // Single trainer or no trainer: use user_id with fallback to current user
    user_id = userId;
    if (!user_id) platform = true;
  }

  const params = {
    platform,
    object_type: type,
    start,
    end,
    time_zone: timeZone,
    user_id,
    user_ids,
    'q[cancelled]': cancelled,
    'q[location]': location,
    'q[noAvailabilitySchedules]': noAvailabilitySchedules,
    'q[spots_remaining]': spotsRemaining,
    'q[service]': serviceId,
    'q[service_tag]': category,
    'q[resource]': resourceId,
    'q[trainer_tag]': trainerTag,
  };
  const response = await api.get('/api/v4/calendar', {
    params,
  });

  const data = camelcaseKeys(response.data, { deep: true });

  return data;
};
